import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Navigation, MapPin, Play, Loader2, PackageX } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CategoryBadge, StatusBadge } from "@/components/Badges";
import { TaskCompleteForm } from "@/pages/staff/TaskCompleteForm";
import { TaskVerifyDialog } from "@/components/TaskVerifyDialog";
import { api, formatErr, getPosition, mapsLink, fmtDateTime } from "@/lib/api";

export default function TaskDetail() {
  const { id } = useParams();
  const [t, setT] = useState(null);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(null);

  useEffect(() => {
    api.get(`/tasks/${id}`).then((r) => setT(r.data)).catch((e) => toast.error(formatErr(e)));
  }, [id]);

  const start = async () => {
    setBusy(true);
    try {
      const pos = await getPosition();
      const { data } = await api.post(`/tasks/${id}/start`, pos);
      setT(data);
      toast.success("Task started · GPS location recorded");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };
  const spares = async () => {
    try {
      const { data } = await api.patch(`/tasks/${id}/status`, { status: "pending_spares" });
      setT(data);
      toast.info("Marked as pending spares");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  if (!t) return <div className="h-40 rounded-xl bg-slate-200 animate-pulse" />;
  return (
    <div className="space-y-5">
      <Link to="/app/tasks" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900" data-testid="task-back-link"><ArrowLeft className="h-4 w-4" />My Tasks</Link>
      <div className="bg-white rounded-2xl border p-5 fade-up">
        <div className="flex items-center gap-2"><CategoryBadge category={t.category} /><StatusBadge status={t.status} testid="task-status-badge" /></div>
        <h1 className="font-heading text-2xl font-extrabold text-slate-900 mt-2 leading-tight" data-testid="task-title">{t.task_title}</h1>
        {t.description && <p className="text-sm text-slate-600 mt-2">{t.description}</p>}
        <div className="mt-4 rounded-xl bg-slate-50 border p-4">
          <div className="font-semibold text-slate-900">{t.client?.hospital_name}</div>
          <div className="text-sm text-slate-600 flex items-start gap-1.5 mt-1"><MapPin className="h-4 w-4 mt-0.5 text-slate-400 shrink-0" />{t.client?.address}, {t.client?.city}</div>
          {t.client?.doctor_contact_person && <div className="text-xs text-slate-500 mt-1">Contact: {t.client.doctor_contact_person} {t.client.phone_number}</div>}
          <a href={mapsLink(t.client)} target="_blank" rel="noreferrer" data-testid="task-google-maps-link"
            className="mt-3 inline-flex items-center gap-2 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-4 py-2 transition-colors">
            <Navigation className="h-4 w-4" />Navigate in Google Maps
          </a>
        </div>
        <div className="text-xs text-slate-500 mt-3">Due {fmtDateTime(t.due_date)} · Assigned by {t.assigned_by_name}</div>
      </div>
      {["assigned", "pending_spares"].includes(t.status) && (
        <Button onClick={start} disabled={busy} className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-base" data-testid="task-start-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Play className="h-4 w-4 mr-2" />Start Task (verify GPS)</>}
        </Button>
      )}
      {t.status === "in_progress" && (
        <>
          <TaskCompleteForm task={t} onDone={setT} />
          <Button variant="outline" onClick={spares} className="w-full" data-testid="task-pending-spares-button"><PackageX className="h-4 w-4 mr-2" />Waiting for spares</Button>
        </>
      )}
      {t.status === "completed" && (
        <Button variant="outline" className="w-full" onClick={() => setView(t)} data-testid="task-view-proof-button">View submitted proof</Button>
      )}
      <TaskVerifyDialog task={view} onOpenChange={() => setView(null)} />
    </div>
  );
}
